import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { discussionService, Comment as BaseComment, CommentCreateData } from '../../api/discussionService';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';

// Default avatar placeholder
const DEFAULT_AVATAR = 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png';

// Category icon and color mapping
const CATEGORY_CONFIG: Record<string, { icon: string; color: string }> = {
  general: { icon: '📢', color: '#6c757d' },
  questions: { icon: '❓', color: '#007bff' },
  testimonials: { icon: '✝️', color: '#28a745' },
  resources: { icon: '📚', color: '#fd7e14' },
  default: { icon: '📝', color: '#6c757d' }
};

// Extend Comment interface to include userHasLiked
interface Comment extends BaseComment {
  userHasLiked?: boolean;
}

const PostDetailContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  margin-bottom: 20px;
  
  &:hover {
    text-decoration: underline;
  }
`;

const PostCard = styled.div`
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 30px;
  margin-bottom: 30px;
`;

const PostTitle = styled.h1`
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 20px;
`;

const PostMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  font-size: 0.9rem;
  color: ${props => props.theme.colors.textLight};
`;

const PostAuthor = styled.div`
  display: flex;
  align-items: center;
`;

const AuthorAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin-right: 10px;
`;

const AuthorName = styled.div`
  font-weight: 600;
  color: ${props => props.theme.colors.text};
`;

const PostDate = styled.span`
  font-style: italic;
`;

const PostContent = styled.div`
  font-size: 1.1rem;
  line-height: 1.7;
  margin-bottom: 20px;
  color: ${props => props.theme.colors.text};
  white-space: pre-wrap;
`;

const PostTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
`;

const Tag = styled.div`
  background-color: ${props => props.theme.colors.bgLight};
  color: ${props => props.theme.colors.text};
  padding: 5px 10px;
  border-radius: 15px;
  font-size: 0.8rem;
`;

const PostFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 15px;
  border-top: 1px solid ${props => props.theme.colors.bgDark};
`;

const PostActions = styled.div`
  display: flex;
  gap: 15px;
`;

const ActionButton = styled.button<{ active?: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.textLight};
  padding: 0;
  font-size: 1rem;
  
  &:hover:not(:disabled) {
    color: ${props => props.theme.colors.primary};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CommentsSection = styled.div`
  margin-top: 40px;
`;

const CommentsSectionTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 20px;
  color: ${props => props.theme.colors.primary};
`;

const CommentForm = styled.form`
  margin-bottom: 30px;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 15px;
  border: 1px solid ${props => props.theme.colors.bgDark};
  border-radius: 8px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  margin-bottom: 10px;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const SubmitButton = styled.button`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 20px;
  cursor: pointer;
  font-weight: 600;
  
  &:disabled {
    background-color: ${props => props.theme.colors.bgDark};
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.primaryDark};
  }
`;

const CommentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const CommentCard = styled.div`
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  padding: 20px;
`;

const CommentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const CommentAuthor = styled.div`
  display: flex;
  align-items: center;
`;

const CommentAuthorAvatar = styled.img`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  margin-right: 10px;
`;

const CommentFooter = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 15px;
`;

const CommentContent = styled.div`
  color: ${props => props.theme.colors.text};
  line-height: 1.6;
  white-space: pre-wrap;
  margin-bottom: 15px;
`;

const ReplyButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ReplyForm = styled.form`
  margin-top: 15px;
  margin-bottom: 15px;
`;

const ReplyList = styled.div`
  margin-top: 20px;
  margin-left: 20px;
  border-left: 2px solid ${props => props.theme.colors.bgDark};
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.danger};
  background-color: ${props => `${props.theme.colors.danger}15`};
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 20px;
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 20px;
  color: ${props => props.theme.colors.textLight};
`;

const CategoryBadge = styled.div<{ bgColor?: string }>`
  background-color: ${props => props.bgColor || '#6c757d'};
  color: white;
  padding: 6px 14px;
  border-radius: 16px;
  font-size: 0.9rem;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 15px;
`;

const PostEditForm = styled.div`
  margin: 1rem 0;
`;

const CommentEditForm = styled.div`
  margin: 0.5rem 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const Button = styled.button<{ $primary?: boolean }>`
  padding: 0.5rem 1rem;
  border-radius: 4px;
  border: 1px solid ${props => props.$primary ? props.theme.colors.primary : props.theme.colors.border};
  background-color: ${props => props.$primary ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.$primary ? '#fff' : props.theme.colors.text};
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    background-color: ${props => props.$primary ? props.theme.colors.primaryDark : props.theme.colors.bgLight};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PostDetail: React.FC = () => {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [commentText, setCommentText] = useState('');
  const [replyToComment, setReplyToComment] = useState<number | null>(null);
  const [replyText, setReplyText] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const [isEditingPost, setIsEditingPost] = useState(false);
  const [editedPostContent, setEditedPostContent] = useState('');
  const [isEditingComment, setIsEditingComment] = useState<number | null>(null);
  const [editedCommentContent, setEditedCommentContent] = useState('');
  
  // Validate post ID
  const postIdNumber = useMemo(() => {
    if (!postId) return null;
    const parsedId = parseInt(postId);
    return isNaN(parsedId) || parsedId <= 0 ? null : parsedId;
  }, [postId]);

  // Get post details
  const { 
    data: postData, 
    isLoading: postLoading, 
    error: postError 
  } = useQuery({
    queryKey: ['post', postIdNumber],
    queryFn: () => {
      if (!postIdNumber) throw new Error('Invalid post ID');
      console.log('Fetching post with ID:', postIdNumber);
      return discussionService.getPostById(postIdNumber);
    },
    enabled: !!postIdNumber && !authLoading,
    retry: 1,
    retryDelay: 1000,
  });

  // Get comments
  const { 
    data: commentsData, 
    isLoading: commentsLoading, 
    error: commentsError 
  } = useQuery({
    queryKey: ['comments', postIdNumber],
    queryFn: () => {
      if (!postIdNumber) throw new Error('Invalid post ID');
      return discussionService.getPostComments(postIdNumber);
    },
    enabled: !!postIdNumber
  });

  // Add comment mutation
  const addCommentMutation = useMutation({
    mutationFn: (commentData: CommentCreateData) => {
      if (!postIdNumber) throw new Error('Invalid post ID');
      return discussionService.createComment({ ...commentData, postId: postIdNumber });
    },
    onSuccess: () => {
      if (postIdNumber) {
        queryClient.invalidateQueries({ queryKey: ['comments', postIdNumber] });
      }
      setCommentText('');
      setReplyToComment(null);
      setReplyText('');
      setAuthError(null);
    },
    onError: (error: any) => {
      console.error('Comment error:', error);
      const errorResponse = error.response;
      const errorData = errorResponse?.data;
      
      console.log('Detailed error info:', {
        status: errorResponse?.status,
        statusText: errorResponse?.statusText,
        data: errorData,
        message: error.message,
        requestURL: error.config?.url,
        requestData: error.config?.data,
        requestMethod: error.config?.method
      });
      
      if (errorResponse?.status === 401) {
        setAuthError('You must be logged in to comment. Please log in and try again.');
      } else if (errorResponse?.status === 400) {
        const errorMessage = errorData?.message || errorData?.error || 'Invalid comment data';
        setAuthError(`Bad request: ${errorMessage}`);
      } else {
        setAuthError(errorData?.message || error.message || 'Failed to post comment. Please try again later.');
      }
      
      setTimeout(() => setAuthError(null), 8000);
    }
  });

  // Like post mutation
  const likePostMutation = useMutation({
    mutationFn: () => {
      if (!postIdNumber) throw new Error('Invalid post ID');
      return discussionService.likePost(postIdNumber);
    },
    onSuccess: () => {
      if (postIdNumber) {
        queryClient.invalidateQueries({ queryKey: ['post', postIdNumber] });
      }
    },
    onError: (error: any) => {
      console.error('Like error:', error);
      if (error?.response?.status === 401) {
        setAuthError('You must be logged in to like posts');
      }
    }
  });

  // Like comment mutation
  const likeCommentMutation = useMutation({
    mutationFn: (commentId: number) => {
      if (!postIdNumber) throw new Error('Invalid post ID');
      return discussionService.likeComment(commentId);
    },
    onSuccess: () => {
      if (postIdNumber) {
        queryClient.invalidateQueries({ queryKey: ['comments', postIdNumber] });
      }
    }
  });

  // Unlike comment mutation
  const unlikeCommentMutation = useMutation({
    mutationFn: (commentId: number) => discussionService.unlikeComment(commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', postIdNumber] });
    }
  });

  // Delete post mutation
  const deletePostMutation = useMutation({
    mutationFn: () => {
      if (!postIdNumber) throw new Error('Invalid post ID');
      return discussionService.deletePost(postIdNumber);
    },
    onSuccess: () => {
      navigate('/community');
    }
  });

  // Update post mutation
  const updatePostMutation = useMutation({
    mutationFn: (updatedContent: string) => {
      if (!postIdNumber) throw new Error('Invalid post ID');
      return discussionService.updatePost(postIdNumber, { content: updatedContent });
    },
    onSuccess: () => {
      if (postIdNumber) {
        queryClient.invalidateQueries({ queryKey: ['post', postIdNumber] });
      }
      setIsEditingPost(false);
    }
  });

  // Delete comment mutation
  const deleteCommentMutation = useMutation({
    mutationFn: (commentId: number) => discussionService.deleteComment(commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', postIdNumber] });
    }
  });

  // Update comment mutation
  const updateCommentMutation = useMutation({
    mutationFn: ({ commentId, content }: { commentId: number; content: string }) => 
      discussionService.updateComment(commentId, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', postIdNumber] });
      setIsEditingComment(null);
    }
  });

  // Organize comments and replies
  const organizedComments = useMemo(() => {
    if (!commentsData?.data?.comments) return [];
    
    const topLevelComments: BaseComment[] = [];
    const repliesByParentId: Record<number, BaseComment[]> = {};
    
    commentsData.data.comments.forEach((comment: BaseComment) => {
      if (comment.parent_id === null) {
        topLevelComments.push({...comment, replies: []});
      } else {
        if (!repliesByParentId[comment.parent_id]) {
          repliesByParentId[comment.parent_id] = [];
        }
        repliesByParentId[comment.parent_id].push(comment);
      }
    });
    
    topLevelComments.forEach(comment => {
      if (repliesByParentId[comment.id]) {
        comment.replies = repliesByParentId[comment.id];
      }
    });
    
    return topLevelComments;
  }, [commentsData]);

  // Redirect if invalid post ID, but only after auth is loaded
  useEffect(() => {
    console.log('Checking post ID:', { postIdNumber, authLoading });
    if (!authLoading && postIdNumber === null) {
      console.log('Redirecting to community due to invalid post ID');
      navigate('/community', { replace: true });
    }
  }, [postIdNumber, navigate, authLoading]);

  // Show loading state while auth or post is loading
  if (authLoading || postLoading) {
    return (
      <PostDetailContainer>
        <div>Loading post...</div>
      </PostDetailContainer>
    );
  }
  
  // Show error state if post fetch failed
  if (postError) {
    console.error('Post fetch error:', postError);
    return (
      <PostDetailContainer>
        <div>Error loading post. Please try again later.</div>
        <BackButton onClick={() => navigate('/community')}>Back to Community</BackButton>
      </PostDetailContainer>
    );
  }
  
  // Show not found state if post doesn't exist
  if (!postData?.data?.post) {
    return (
      <PostDetailContainer>
        <div>Post not found.</div>
        <BackButton onClick={() => navigate('/community')}>Back to Community</BackButton>
      </PostDetailContainer>
    );
  }
  
  const { post, userHasLiked } = postData.data;
  const comments = commentsData?.data?.comments || [];
  const rootComments = comments.filter((comment: Comment) => !comment.parent_id);
  
  // Organize comments and replies
  const commentReplies: Record<number, Comment[]> = {};
  comments.filter((comment: Comment) => comment.parent_id).forEach((reply: Comment) => {
    const parentId = reply.parent_id as number;
    if (!commentReplies[parentId]) {
      commentReplies[parentId] = [];
    }
    commentReplies[parentId].push(reply);
  });
  
  // Helper functions
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const getCategoryConfig = (category: string) => {
    const normalizedCategory = category?.toLowerCase();
    return CATEGORY_CONFIG[normalizedCategory] || CATEGORY_CONFIG.default;
  };

  const isCurrentUserOwner = (ownerId: number) => {
    if (!user?.id) return false;
    return ownerId === parseInt(user.id);
  };

  const toggleLikeComment = (comment: Comment) => {
    if (comment.userHasLiked) {
      unlikeCommentMutation.mutate(comment.id);
    } else {
      likeCommentMutation.mutate(comment.id);
    }
  };

  const handleReplySubmit = (parentId: number) => {
    if (!isAuthenticated) {
      setAuthError('You must be logged in to reply');
      return;
    }
    
    if (!replyText.trim() || !postIdNumber) return;
    
    addCommentMutation.mutate({
      content: replyText,
      postId: postIdNumber,
      parentId
    });
  };

  const handleLikePost = () => {
    if (!isAuthenticated || !postIdNumber) {
      setAuthError('You must be logged in to like posts');
      return;
    }
    
    likePostMutation.mutate();
  };

  const handleCommentSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAuthenticated || !postIdNumber) {
      setAuthError('You must be logged in to comment');
      return;
    }
    
    if (!commentText.trim()) return;
    
    addCommentMutation.mutate({
      content: commentText,
      postId: postIdNumber
    });
  };

  const handleLikeComment = (commentId: number) => {
    if (!isAuthenticated || !postIdNumber) {
      setAuthError('You must be logged in to like comments');
      return;
    }
    
    likeCommentMutation.mutate(commentId);
  };

  const handleEditPost = () => {
    if (!isAuthenticated || !postIdNumber) {
      setAuthError('You must be logged in to edit posts');
      return;
    }
    if (!postData?.data?.post || !isCurrentUserOwner(postData.data.post.user_id)) {
      setAuthError('You can only edit your own posts');
      return;
    }
    setEditedPostContent(postData.data.post.content);
    setIsEditingPost(true);
  };

  const handleDeletePost = () => {
    if (!isAuthenticated || !postIdNumber) {
      setAuthError('You must be logged in to delete posts');
      return;
    }
    if (!postData?.data?.post || !isCurrentUserOwner(postData.data.post.user_id)) {
      setAuthError('You can only delete your own posts');
      return;
    }
    if (window.confirm('Are you sure you want to delete this post?')) {
      deletePostMutation.mutate();
    }
  };

  const handleUpdatePost = () => {
    if (!editedPostContent.trim()) return;
    updatePostMutation.mutate(editedPostContent);
  };

  const handleEditComment = (comment: Comment) => {
    if (!isAuthenticated) {
      setAuthError('You must be logged in to edit comments');
      return;
    }
    if (!isCurrentUserOwner(comment.user_id)) {
      setAuthError('You can only edit your own comments');
      return;
    }
    setEditedCommentContent(comment.content);
    setIsEditingComment(comment.id);
  };

  const handleDeleteComment = (comment: Comment) => {
    if (!isAuthenticated) {
      setAuthError('You must be logged in to delete comments');
      return;
    }
    if (!isCurrentUserOwner(comment.user_id)) {
      setAuthError('You can only delete your own comments');
      return;
    }
    if (window.confirm('Are you sure you want to delete this comment?')) {
      deleteCommentMutation.mutate(comment.id);
    }
  };

  const handleUpdateComment = (commentId: number) => {
    if (!editedCommentContent.trim()) return;
    updateCommentMutation.mutate({ commentId, content: editedCommentContent });
  };

  const handleReplyFormSubmit = (e: React.FormEvent, parentId: number) => {
    e.preventDefault();
    if (replyToComment) {
      handleReplySubmit(parentId);
    }
  };

  return (
    <PostDetailContainer>
      {authError && (
        <ErrorMessage>
          {authError}
        </ErrorMessage>
      )}
      
      <BackButton onClick={() => navigate('/community')}>
        ← Back to community
      </BackButton>
      
      <PostCard>
        <PostTitle>{post.title}</PostTitle>
        <PostMeta>
          <PostAuthor>
            <AuthorAvatar 
              src={post.author_avatar_url || DEFAULT_AVATAR} 
              alt={post.author_display_name}
            />
            <div>
              <AuthorName>
                {post.author_display_name || 
                 (post.author_first_name || post.author_last_name ? 
                  `${post.author_first_name || ''} ${post.author_last_name || ''}`.trim() : 
                  post.author_username || 'Anonymous User')}
              </AuthorName>
              <div>@{post.author_username}</div>
            </div>
          </PostAuthor>
          <PostDate>{formatDate(post.created_at)}</PostDate>
        </PostMeta>
        
        <CategoryBadge bgColor={getCategoryConfig(post.category).color}>
          {getCategoryConfig(post.category).icon} {post.category}
        </CategoryBadge>
        
        {isEditingPost ? (
          <PostEditForm>
            <TextArea
              value={editedPostContent}
              onChange={(e) => setEditedPostContent(e.target.value)}
              required
            />
            <ButtonGroup>
              <Button onClick={() => setIsEditingPost(false)}>Cancel</Button>
              <Button $primary onClick={handleUpdatePost} disabled={updatePostMutation.isPending}>
                {updatePostMutation.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </ButtonGroup>
          </PostEditForm>
        ) : (
          <PostContent>{post.content}</PostContent>
        )}
        
        <PostTags>
          {post.tags.map((tag: string) => (
            <Tag key={tag}>{tag}</Tag>
          ))}
        </PostTags>
        
        <PostFooter>
          <PostActions>
            {(() => {
              console.log('Rendering post actions:', {
                isAuthenticated,
                postUserId: post.user_id,
                currentUserId: user?.id,
                isOwner: isCurrentUserOwner(post.user_id)
              });
              return (
                <>
                  <ActionButton 
                    active={userHasLiked} 
                    onClick={handleLikePost}
                    disabled={likePostMutation.isPending}
                  >
                    ❤️ {post.likes_count} {post.likes_count === 1 ? 'Like' : 'Likes'}
                  </ActionButton>
                  <ActionButton>
                    💬 {post.comments_count} {post.comments_count === 1 ? 'Comment' : 'Comments'}
                  </ActionButton>
                  {isAuthenticated && isCurrentUserOwner(post.user_id) && (
                    <>
                      <ActionButton onClick={handleEditPost}>
                        ✏️ Edit
                      </ActionButton>
                      <ActionButton onClick={handleDeletePost}>
                        🗑️ Delete
                      </ActionButton>
                    </>
                  )}
                </>
              );
            })()}
          </PostActions>
        </PostFooter>
      </PostCard>
      
      <CommentsSection>
        <CommentsSectionTitle>Comments</CommentsSectionTitle>
        
        <CommentForm onSubmit={handleCommentSubmit}>
          <TextArea
            placeholder={isAuthenticated ? "Write a comment..." : "Log in to comment"}
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            required
            disabled={!isAuthenticated}
          />
          <SubmitButton 
            type="submit" 
            disabled={addCommentMutation.isPending || !commentText.trim() || !isAuthenticated}
          >
            {!isAuthenticated ? 'Log in to Comment' : addCommentMutation.isPending ? 'Posting...' : 'Post Comment'}
          </SubmitButton>
        </CommentForm>
        
        {commentsLoading ? (
          <LoadingText>Loading comments...</LoadingText>
        ) : comments.length > 0 ? (
          <CommentsList>
            {organizedComments.map((comment: Comment) => (
              <CommentCard key={comment.id}>
                <CommentHeader>
                  <CommentAuthor>
                    <CommentAuthorAvatar 
                      src={comment.author_avatar_url || DEFAULT_AVATAR} 
                      alt={comment.author_display_name}
                    />
                    <div>
                      <strong>
                        {comment.author_display_name || 
                         (comment.author_first_name || comment.author_last_name ? 
                          `${comment.author_first_name || ''} ${comment.author_last_name || ''}`.trim() : 
                          comment.author_username || 'Anonymous User')}
                      </strong>
                      <div>{formatDate(comment.created_at)}</div>
                    </div>
                  </CommentAuthor>
                </CommentHeader>
                
                {isEditingComment === comment.id ? (
                  <CommentEditForm>
                    <TextArea
                      value={editedCommentContent}
                      onChange={(e) => setEditedCommentContent(e.target.value)}
                      required
                    />
                    <ButtonGroup>
                      <Button onClick={() => setIsEditingComment(null)}>Cancel</Button>
                      <Button $primary onClick={() => handleUpdateComment(comment.id)} disabled={updateCommentMutation.isPending}>
                        {updateCommentMutation.isPending ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </ButtonGroup>
                  </CommentEditForm>
                ) : (
                  <CommentContent>{comment.content}</CommentContent>
                )}
                
                <CommentFooter>
                  <div>
                    <ActionButton 
                      active={comment.userHasLiked}
                      onClick={() => handleLikeComment(comment.id)}
                      disabled={likeCommentMutation.isPending || unlikeCommentMutation.isPending}
                    >
                      ❤️ {comment.likes_count}
                    </ActionButton>
                  </div>
                  {isAuthenticated && (
                    <div>
                      {isCurrentUserOwner(comment.user_id) && (
                        <>
                          <ActionButton onClick={() => handleEditComment(comment)}>
                            ✏️ Edit
                          </ActionButton>
                          <ActionButton onClick={() => handleDeleteComment(comment)}>
                            🗑️ Delete
                          </ActionButton>
                        </>
                      )}
                    </div>
                  )}
                  <ReplyButton 
                    onClick={() => isAuthenticated 
                      ? setReplyToComment(replyToComment === comment.id ? null : comment.id)
                      : navigate('/login', { state: { from: `/community/posts/${postId}` } })
                    }
                  >
                    {!isAuthenticated ? 'Log in to Reply' : replyToComment === comment.id ? 'Cancel Reply' : 'Reply'}
                  </ReplyButton>
                </CommentFooter>
                
                {replyToComment === comment.id && (
                  <ReplyForm onSubmit={(e) => handleReplyFormSubmit(e, comment.id)}>
                    <TextArea
                      placeholder="Write a reply..."
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      required
                    />
                    <SubmitButton 
                      type="submit"
                      disabled={addCommentMutation.isPending || !replyText.trim()}
                    >
                      {addCommentMutation.isPending ? 'Posting...' : 'Post Reply'}
                    </SubmitButton>
                  </ReplyForm>
                )}
                
                {comment.replies && comment.replies.length > 0 && (
                  <ReplyList>
                    {comment.replies.map((reply: Comment) => (
                      <CommentCard key={reply.id}>
                        <CommentHeader>
                          <CommentAuthor>
                            <CommentAuthorAvatar 
                              src={reply.author_avatar_url || DEFAULT_AVATAR} 
                              alt={reply.author_display_name}
                            />
                            <div>
                              <strong>
                                {reply.author_display_name || 
                                 (reply.author_first_name || reply.author_last_name ? 
                                  `${reply.author_first_name || ''} ${reply.author_last_name || ''}`.trim() : 
                                  reply.author_username || 'Anonymous User')}
                              </strong>
                              <div>{formatDate(reply.created_at)}</div>
                            </div>
                          </CommentAuthor>
                        </CommentHeader>
                        
                        <CommentContent>{reply.content}</CommentContent>
                        
                        <CommentFooter>
                          <ActionButton 
                            active={reply.userHasLiked}
                            onClick={() => handleLikeComment(reply.id)}
                            disabled={likeCommentMutation.isPending || unlikeCommentMutation.isPending}
                          >
                            ❤️ {reply.likes_count}
                          </ActionButton>
                        </CommentFooter>
                      </CommentCard>
                    ))}
                  </ReplyList>
                )}
              </CommentCard>
            ))}
          </CommentsList>
        ) : (
          <div>No comments yet. Be the first to share your thoughts!</div>
        )}
      </CommentsSection>
    </PostDetailContainer>
  );
};

export default PostDetail; 